/**
 * <s-newsletter-popup>: opens the 10% welcome dialog once per visitor.
 * - Waits `data-delay` seconds, then opens only when no other dialog is open.
 * - Closing it hides it for `data-dismiss-days` days; signing up hides it for good.
 * - After a successful or failed submit the page reloads with the result, so the
 *   dialog reopens straight away to show the message or error.
 * - Never auto-opens inside the theme editor.
 */
const STORAGE_KEY = 'bb-newsletter-popup';

class NewsletterPopup extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dialog.addEventListener('close', this.#onClose);
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit);

    const success = this.querySelector('[data-popup-success]');
    const error = this.querySelector('[data-popup-error]');

    if (success) {
      this.#store({ state: 'subscribed' });
      if (this.#cameFromPopup()) this.#open();
      return;
    }
    if (error && this.#cameFromPopup()) {
      this.#open();
      return;
    }
    if (window.Shopify?.designMode || !this.#shouldShow()) return;

    const delay = Number(this.dataset.delay || 15) * 1000;
    this.#timer = window.setTimeout(this.#tryOpen, delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.dialog?.removeEventListener('close', this.#onClose);
  }

  #tryOpen = () => {
    const otherOpen = document.querySelector('dialog[open]:not(#newsletter-popup)');
    if (otherOpen || document.visibilityState !== 'visible') {
      this.#timer = window.setTimeout(this.#tryOpen, 5000);
      return;
    }
    this.#open();
  };

  #open() {
    if (!this.dialog.open) this.dialog.showModal();
  }

  #onClose = () => {
    const saved = this.#read();
    if (saved?.state !== 'subscribed') this.#store({ state: 'dismissed', at: Date.now() });
  };

  #onSubmit = () => {
    this.#store({ state: 'submitted', at: Date.now() });
  };

  #cameFromPopup() {
    return window.location.hash === '#newsletter-popup-form' || this.#read()?.state === 'submitted';
  }

  #shouldShow() {
    const saved = this.#read();
    if (!saved) return true;
    if (saved.state === 'subscribed' || saved.state === 'submitted') return false;
    const days = Number(this.dataset.dismissDays || 30);
    return Date.now() - (saved.at || 0) > days * 86400000;
  }

  #read() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      return null;
    }
  }

  #store(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* storage unavailable: the popup simply behaves per page view */
    }
  }
}

if (!customElements.get('s-newsletter-popup')) {
  customElements.define('s-newsletter-popup', NewsletterPopup);
}
